import { createFileRoute, useSearch } from "@tanstack/react-router";
import { Helmet } from "react-helmet-async";

import { SpaceEditor } from "@/components/portal/spaces/SpaceEditor";
import { RequirePermission } from "@/components/portal/RequirePermission";
import type { SpaceSurface } from "@/services/api/spaces";

/**
 * Create a new space. Mounts the SpaceEditor in create mode (no `initial`);
 * on save it POSTs to the CP spaces endpoint and navigates to the new space's
 * view. Platform-admin-gated for now (Spaces isn't a general portal entry yet).
 *
 * The optional `?surface=` search param tags the new space with the surface it
 * was created under (e.g. `reporting` from the reporting entry) so it lands
 * back in that surface's list rather than the default `portal`.
 */
type NewSpaceSearch = { surface?: SpaceSurface };

export const Route = createFileRoute("/portal/spaces/new")({
  component: NewSpacePage,
  validateSearch: (search: Record<string, unknown>): NewSpaceSearch => {
    const surface = search.surface;
    return surface === "reporting" || surface === "portal"
      ? { surface }
      : {};
  },
});

function NewSpacePage() {
  const { surface } = useSearch({ from: "/portal/spaces/new" });
  return (
    <>
      <Helmet>
        <title>New space</title>
      </Helmet>
      <RequirePermission resource="spaces" action="create">
        <SpaceEditor defaultSurface={surface} />
      </RequirePermission>
    </>
  );
}
